import { use, useEffect, useRef, useState } from 'react';
import { s } from './ResultReport.styles';
import Summary from './Summary';
import ResultDetail from './ResultDetail';
import { usePatternContext } from '../../../../../context/PatternContext';
import PatternService from '../../../../../service/Pattern';
import AddInterestItemModal from '../../../../components/modal/AddInterestItem';
import List from '../../../../components/list/List';
import { FilterType, Region } from '../../../../search/FilterData';
import styled from 'styled-components';
import Loading from '../../../../../func/components/loading/LoadingCircle';

type SortOption = { value: string; label: string };
const ResultReport = () => {
  const [data, setData] = useState([]);
  const [itemList, setItemList] = useState([]);
  const { pIdx, setPatternInfo } = usePatternContext();
  const [total, setTotal] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [loading, setLoading] = useState(false);
  const [showLikeModal, setShowLikeModal] = useState<boolean>(false);
  const [mode, setMode] = useState<string>('add');
  const [selectedIdx, setSelectedIdx] = useState<number>(0);
  const [activeItem, setActiveItem] = useState<number | null>(null);
  const [selectedItem, setSelectedItem] = useState({
    iidx: 0,
    group: '',
    group_idx: 0,
    level: 0,
    memo: '',
  });
  const [sort, setSort] = useState<SortOption>({
    value: 'DA',
    label: '매각기일 빠른순',
  });

  const getPatternResult = async (pIdx: number) => {
    try {
      const res = await PatternService.getPatternResult(pIdx);
      setData(res.data.analysis);
      setPatternInfo(res.data.patternInfo);
    } catch (error) {
      console.log(error);
    }
  };

  const getList = async (
    offset: number,
    sort: {
      value: string;
      label: string;
    },
  ) => {
    try {
      const res = await PatternService.list(
        pIdx,
        sort.value,
        limit,
        offset,
        'A',
        'A',
        'A',
      );
      setItemList(res.data.list);
      setTotal(res.data.totalNumber);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (pIdx !== 0) {
        setLoading(true);
        await getPatternResult(pIdx);
        await getList(offset, sort);
        setLoading(false);
      }
    };

    fetchData();
  }, [pIdx]);

  const [filters, setFilters] = useState<FilterType>({
    type: 'a',
    regionOrCourt: 'r',
    region: [] as Region[],
    court: '',
    itemStatus: [],
    multiItemStatus: [],
    appraisalPrice: { min: '', max: '' },
    minPrice: { min: '', max: '' },
    saleDate: { start: '', end: '' },
    approvalDate: { start: '', end: '' },
    buildingArea: { min: '', max: '' },
    landArea: { min: '', max: '' },
    bidStatus: '',
    progressStatus: '진행',
    useArea: [],
    multiUseArea: [],
    specialConditionViewOption: 'o',
    specialItemCondition: [],
    multiSpecialItemCondition: [],
    holdingCompany: [],
    multiHoldingCompany: [],
  });

  const listRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const [currentTab, setCurrentTab] = useState('분석결과');

  // listRef의 위치를 구해서 currentTab을 변경하는 함수

  useEffect(() => {
    const handleScroll = () => {
      if (listRef.current && detailRef.current && resultRef.current) {
        const listRect = listRef.current.getBoundingClientRect();
        const detailRect = detailRef.current.getBoundingClientRect();
        const resultRect = resultRef.current.getBoundingClientRect();
        if (listRect.top < 200) {
          setCurrentTab('추천물건');
        } else if (detailRect.top < 130) {
          setCurrentTab('상세분석');
        } else if (resultRect.top < 130) {
          setCurrentTab('분석결과');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (pIdx !== 0 && offset !== 0) {
      getList(offset, sort);
      if (!listRef.current) return;
      window.scrollTo({
        top: listRef.current?.offsetTop - 100,
        behavior: 'smooth',
      });
    }
  }, [offset]);

  const prevSort = useRef(sort);

  useEffect(() => {
    if (prevSort.current.value !== sort.value) {
      setOffset(0);
      getList(0, sort);
    }
    prevSort.current = sort;
  }, [sort]);

  return (
    <Container>
      {loading && <Loading />}
      {!loading && (
        <>
          <TitleBox>
            <TitleContent
              onClick={() => {
                setCurrentTab('분석결과');
                if (!resultRef.current) return;
                window.scrollTo({
                  top: resultRef.current?.offsetTop - 100,
                  behavior: 'smooth',
                });
              }}
              isActive={currentTab === '분석결과'}
            >
              분석결과
            </TitleContent>
            <TitleContent
              onClick={() => {
                setCurrentTab('상세분석');
                if (!detailRef.current) return;
                window.scrollTo({
                  top: detailRef.current?.offsetTop - 100,
                  behavior: 'smooth',
                });
              }}
              isActive={currentTab === '상세분석'}
            >
              상세분석
            </TitleContent>
            <TitleContent
              onClick={() => {
                setCurrentTab('추천물건');
                if (!listRef.current) return;
                window.scrollTo({
                  top: listRef.current?.offsetTop - 100,
                  behavior: 'smooth',
                });
              }}
              isActive={currentTab === '추천물건'}
            >
              추천물건
            </TitleContent>
          </TitleBox>
          <Inner>
            {data.length > 0 && (
              <div ref={resultRef}>
                <Summary data={data} />
              </div>
            )}
            {data.length > 0 && (
              <div ref={detailRef}>
                <ResultDetail data={data} />
              </div>
            )}
            <div ref={listRef}>
              <List
                data={itemList}
                totalCount={total}
                sort={sort}
                setSort={setSort}
                setOffset={setOffset}
                filters={filters}
                limit={limit}
                loading={loading}
                page="search"
                setSelectedIdx={setSelectedIdx}
                setShowLikeModal={setShowLikeModal}
                setMode={setMode}
                setSelectedItem={setSelectedItem}
                activeItem={activeItem}
                setActiveItem={setActiveItem}
                getList={getList}
              />
            </div>
          </Inner>
        </>
      )}

      {showLikeModal && (
        <AddInterestItemModal
          setShowLikeModal={setShowLikeModal}
          selectedIdx={selectedIdx}
          mode={mode}
          selectedItem={selectedItem}
          getList={getList}
          offset={offset}
          sort={sort}
          filters={filters}
          setActiveItem={setActiveItem}
        />
      )}
    </Container>
  );
};

export default ResultReport;

interface TitleContentProps {
  isActive: boolean;
}

const TitleContent = styled.div<TitleContentProps>`
  width: 100%;
  font-size: 16px;
  font-family: Spoqa Han Sans Neo;
  font-weight: 500;
  line-height: 23.2px;
  word-wrap: break-word;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 0px;
  // color: #222222;
  // border-bottom: 1px solid #222;
  color: ${(props) => (props.isActive ? '#222' : '#989BA2')};
  border-bottom: ${(props) => (props.isActive ? '1px solid #222' : 'none')};
`;

const TitleBox = styled.div`
  position: sticky;
  top: 45px;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 24px 16px 0px;
  border-bottom: 1px solid #e5e8ea;
  border-radius: 16px 16px 0px 0px;
  background-color: #fff;
  z-index: 11;
`;

const Inner = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: #f7f8fc;
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  background: #fff;
  border-radius: 16px 16px 0px 0px;
`;
